import { useMemo, useRef, type RefObject } from 'react';
import { useChatStore } from '../../store/chat';
import { buildWelcomeMessage } from '../../lib/format';
import { BotBubble } from './BotBubble';
import { UserBubble } from './UserBubble';
import { ToolStatusBubble } from './ToolStatusBubble';
import { TypingIndicator } from './TypingIndicator';
import { QuickReplies } from './QuickReplies';
import type { Message } from '../../types/agent';

const WELCOME_TS = Date.now();

export function MessageList({ bottomRef }: { bottomRef?: RefObject<HTMLDivElement | null> }) {
  const messages = useChatStore((s) => s.messages);
  const connectionState = useChatStore((s) => s.connectionState);
  const pendingToolCallId = useChatStore((s) => s.pendingToolCallId);
  const ownBottomRef = useRef<HTMLDivElement | null>(null);
  const resolvedBottomRef = bottomRef ?? ownBottomRef;

  const welcomeText = useMemo(() => buildWelcomeMessage(), []);
  const welcomeMessage: Message = {
    id: 'welcome',
    role: 'assistant',
    text: welcomeText,
    timestamp: WELCOME_TS,
    deliveryState: 'read',
  };

  const showTyping = connectionState === 'streaming' && pendingToolCallId === null;

  return (
    <div
      role="log"
      aria-live="polite"
      className="wa-scroll flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-[2px] relative"
    >
      <div className="flex flex-col items-start mb-1">
        <BotBubble message={welcomeMessage} />
        {/* Onboarding: botões de resposta rápida estilo WhatsApp, some após a 1ª mensagem. */}
        {messages.length === 0 && (
          <QuickReplies
            replies={[
              { label: '💊 Quero um orçamento', value: 'Oi! Quero um orçamento de uma fórmula manipulada.' },
              { label: '📷 Como envio minha receita?', value: 'Como envio minha receita?' },
            ]}
          />
        )}
      </div>

      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'} mb-1`}
        >
          {message.role === 'user' && <UserBubble message={message} />}
          {message.role === 'assistant' && <BotBubble message={message} />}
          {message.role === 'tool_status' && <ToolStatusBubble message={message} />}
          {message.quickReplies && message.quickReplies.length > 0 && (
            <QuickReplies replies={message.quickReplies} />
          )}
        </div>
      ))}

      {showTyping && (
        <div className="flex justify-start mb-1">
          <TypingIndicator />
        </div>
      )}

      <div ref={resolvedBottomRef} aria-hidden="true" style={{ height: 0 }} />
    </div>
  );
}
